import { Grid, InputWrapper, SimpleGrid, Text } from '@mantine/core'
import type { Patient, RelatedPerson } from '@medplum/fhirtypes'
import { CodeableConceptDisplay, HumanNameDisplay } from '@medplum/react'
import * as patients from '@seen/core/models/patients'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { createReference } from '@seen/core/references'
import { officialNameOrFallback } from '@seen/core/utils'
import { cn } from '@seen/ui'
import { isEmpty } from 'lodash-es'
import type { JSX } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'

type Props = {
  sdr: SDR
  patient: Patient
}

export function SDRRequesterContactInfo({ sdr, patient }: Props): JSX.Element {
  const relatedPersonsQuery = useFhirSearch('RelatedPerson', {
    patient,
  })

  return (
    <FhirQueryLoader query={relatedPersonsQuery}>
      {(relatedPersonsArr) => {
        let requester: Patient | RelatedPerson | undefined
        let otherContacts: (Patient | RelatedPerson)[]
        if (sdr.participantRequester) {
          requester = patient
          otherContacts = relatedPersonsArr
        } else {
          requester = relatedPersonsArr.find(
            (rp) =>
              createReference(rp).reference ===
              sdr.relatedPersonRequester?.reference
          )
          otherContacts = relatedPersonsArr.filter(
            (rp) =>
              createReference(rp).reference !==
              sdr.relatedPersonRequester?.reference
          )
          otherContacts.unshift(patient)
        }

        return (
          <div className="flex flex-col gap-4">
            <Text className="text-lg font-semibold">
              Requester Contact Info
            </Text>

            {requester ? (
              <>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <InputWrapper label="Requested by" className="grow">
                    <div>
                      <HumanNameDisplay
                        value={officialNameOrFallback(requester)}
                      />
                    </div>
                  </InputWrapper>

                  <InputWrapper label="Relationship" className="grow">
                    <Text>
                      {requester.resourceType === 'Patient' ? (
                        'Participant'
                      ) : (
                        <CodeableConceptDisplay
                          value={requester.relationship?.[0]}
                        />
                      )}
                    </Text>
                  </InputWrapper>

                  <InputWrapper label="Email" className="grow">
                    <Text>{patients.getPrimaryEmail(requester) ?? '—'}</Text>
                  </InputWrapper>

                  <InputWrapper label="Phone" className="grow">
                    <Text>
                      {patients.getPrimaryPhoneNumber(requester) ?? '—'}
                    </Text>
                  </InputWrapper>
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
                  {(() => {
                    const address = patients.homeAddressOrFallback(requester)
                    if (!address) {
                      return null
                    }
                    return (
                      <>
                        <InputWrapper label="Street Address" className="grow">
                          <Text>{address.line?.join(', ') ?? '—'}</Text>
                        </InputWrapper>
                        <InputWrapper label="City" className="grow">
                          <Text>{address.city ?? '—'}</Text>
                        </InputWrapper>
                        <InputWrapper label="State" className="grow">
                          <Text>{address.state ?? '—'}</Text>
                        </InputWrapper>
                        <InputWrapper label="Postal Code" className="grow">
                          <Text>{address.postalCode ?? '—'}</Text>
                        </InputWrapper>
                        <InputWrapper label="Country" className="grow">
                          {/* TODO: Make sure country is properly populated after SRR */}
                          <Text>{address.country ?? 'USA'}</Text>
                        </InputWrapper>
                      </>
                    )
                  })()}
                </SimpleGrid>
              </>
            ) : (
              <div>
                We could not load the requester contact info, but the other
                contacts for this participant are presented below.
              </div>
            )}

            {!isEmpty(otherContacts) && (
              <div className="flex flex-col gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 p-4">
                <Text className="text-lg font-semibold">Other Contacts</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {otherContacts.map((contact, index, arr) => (
                    <div
                      key={index}
                      className={cn({
                        'border-0 border-gray-200 border-r border-solid':
                          index !== arr.length - 1,
                      })}
                    >
                      <Grid>
                        <Grid.Col span={3} className="font-semibold">
                          Name
                        </Grid.Col>
                        <Grid.Col span={9}>
                          <HumanNameDisplay
                            value={officialNameOrFallback(contact)}
                          />
                        </Grid.Col>
                        <Grid.Col span={3} className="font-semibold">
                          Relation
                        </Grid.Col>
                        <Grid.Col span={9}>
                          {contact.resourceType === 'Patient' ? (
                            'Participant'
                          ) : (
                            <CodeableConceptDisplay
                              value={contact.relationship?.[0]}
                            />
                          )}
                        </Grid.Col>

                        <Grid.Col span={3} className="font-semibold">
                          Email
                        </Grid.Col>
                        <Grid.Col span={9}>
                          {patients.getPrimaryEmail(contact) ?? '—'}
                        </Grid.Col>

                        <Grid.Col span={3} className="font-semibold">
                          Phone
                        </Grid.Col>
                        <Grid.Col span={9}>
                          {patients.getPrimaryPhoneNumber(contact) ?? '—'}
                        </Grid.Col>

                        <Grid.Col span={3} className="font-semibold">
                          Address
                        </Grid.Col>
                        <Grid.Col span={9}>
                          {patients.homeAddressOrFallback(contact)
                            ? `${patients
                                .homeAddressOrFallback(contact)
                                ?.line?.join(
                                  ', '
                                )}, ${patients.homeAddressOrFallback(contact)?.city}, ${patients.homeAddressOrFallback(contact)?.state}, ${patients.homeAddressOrFallback(contact)?.country}, ${
                                patients.homeAddressOrFallback(contact)
                                  ?.postalCode
                              }`
                            : '—'}
                        </Grid.Col>
                      </Grid>
                    </div>
                  ))}
                </SimpleGrid>
              </div>
            )}
          </div>
        )
      }}
    </FhirQueryLoader>
  )
}
